import { colors } from './colors'

export const typography = {
  family: "'Plus Jakarta Sans', sans-serif",
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  size: {
    display: '40px',
    h1: '32px',
    h2: '24px',
    h3: '20px',
    bodyLarge: '18px',
    body: '16px',
    bodySmall: '14px',
    caption: '12px',
  },
  lineHeight: {
    display: 1.15,
    heading: 1.25,
    body: 1.5,
    compact: 1.35,
  },
} as const

export const spacing = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  6: '24px',
  8: '32px',
  12: '48px',
  16: '64px',
} as const

export const radii = {
  small: '8px',
  medium: '12px',
  large: '16px',
  xlarge: '24px',
  pill: '999px',
  round: '50%',
} as const

export const shadows = {
  none: 'none',
  small:
    '0 2px 8px color-mix(in srgb, var(--redesign-shadow, #0A0A18) 50%, transparent)',
  medium:
    '0 8px 24px color-mix(in srgb, var(--redesign-shadow, #0A0A18) 75%, transparent)',
  large: '0 16px 40px var(--redesign-shadow, rgba(10, 10, 24, 0.12))',
  focus: '0 0 0 3px var(--redesign-focus, rgba(8, 168, 210, 0.24))',
} as const

export const breakpoints = {
  mobile: '480px',
  tablet: '768px',
  desktop: '1024px',
  wide: '1280px',
} as const

export const motion = {
  fast: '120ms',
  normal: '200ms',
  slow: '320ms',
  easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
} as const

export const layout = {
  contentMaxWidth: '1280px',
  contentGutter: 'clamp(16px, 3vw, 32px)',
  headerHeight: '72px',
  sidebarWidthCollapsed: '88px',
  sidebarWidthExpanded: '264px',
  controlHeight: '44px',
  controlHeightLarge: '48px',
} as const

export const semanticColors = {
  background: {
    canvas: colors.background,
    surface: colors.surface,
    elevated: colors.surfaceElevated,
    editorial: 'var(--redesign-surface-editorial, #171824)',
  },
  text: {
    primary: colors.textPrimary,
    secondary: colors.textSecondary,
    muted: colors.textMuted,
  },
  border: {
    default: colors.border,
    strong: colors.borderStrong,
  },
  focus: 'var(--redesign-focus, rgba(8, 168, 210, 0.34))',
  overlay: 'var(--redesign-overlay, rgba(10, 10, 24, 0.42))',
  shadow: 'var(--redesign-shadow, rgba(10, 10, 24, 0.12))',
} as const

export const effects = {
  surfaceOverlay: 'rgba(255, 255, 255, 0.56)',
  surfaceHover: 'rgba(255, 255, 255, 0.86)',
  surfaceActive: 'rgba(255, 255, 255, 0.9)',
  surfaceSoft: 'rgba(255, 255, 255, 0.62)',
  borderOverlay: 'rgba(10, 10, 24, 0.12)',
  borderLight: 'rgba(255, 255, 255, 0.72)',
  focusOverlay: 'rgba(10, 10, 24, 0.22)',
  focusLight: 'rgba(255, 255, 255, 0.62)',
  scrim: 'rgba(10, 10, 24, 0.08)',
  shadowOverlay: 'rgba(10, 10, 24, 0.12)',
  errorFocus: 'rgba(216, 74, 74, 0.18)',
  collageWash: 'rgba(189, 221, 235, 0.16)',
} as const

export const roleColors = {
  student: {
    main: colors.brandYellow,
    hover: '#F2C928',
    strong: colors.brandYellowDark,
    soft: 'var(--redesign-student-soft, #FFF1B3)',
    text: colors.black,
    focus: 'rgba(255, 220, 80, 0.38)',
  },
  professor: {
    main: colors.brandBlue,
    hover: colors.brandBlueDark,
    strong: colors.brandBlueDark,
    soft: colors.brandBlueLight,
    text: colors.white,
    focus: 'rgba(8, 168, 210, 0.28)',
  },
  admin: {
    main: colors.ppalAdmin,
    hover: '#A8D3E3',
    strong: '#39758D',
    soft: '#EFF8FB',
    text: colors.black,
    focus: 'rgba(189, 221, 235, 0.52)',
  },
} as const

export type RoleTone = keyof typeof roleColors

export const theme = {
  colors,
  semanticColors,
  effects,
  roleColors,
  typography,
  spacing,
  radii,
  shadows,
  breakpoints,
  motion,
  layout,
} as const

export type AppTheme = typeof theme
